import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';

// API base - adjust if needed or use .env
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const appointmentTypes = [
  { code: 'A1', label: 'HouseHold' },
  { code: 'A2', label: 'Essential Kit' },
  { code: 'A3', label: 'Safety Kit' },
];

const emptyForm = {
  id: null,
  appointmentType: '',
  name: '',
  gender: 'Male',
  mobile: '',
  aadhar: '',
  registrationNo: '',
  vidhansabha: 'Karad South',
  karad: { village: '', ganName: '', zpName: '', reference: '' },
  other: { village: '', talika: '', district: '' },
  appointmentLocation: '',
  appointmentDate: '',
  godownVisitDate: '',
  godownLocation: 'Nandagaon',
  campLocation: '',
  operatorName: '',
};

export default function Appointment({ userid }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ appointmentType: '', name: '', mobile: '', aadhar: '', vidhansabha: '', appointmentDate: '', godownVisitDate: '', godownLocation: '' });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/appointments`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch: ${res.status}`);
      }
      const data = await res.json();
      setRecords(data.map(mapFromApi));
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  }, []);

  const mapFromApi = (r) => {
    const isKarad = r.vidhansabha === 'Karad South';
    return {
      id: r.id,
      appointmentType: r.appointment_type || '',
      name: r.name || '',
      gender: r.gender || 'Male',
      mobile: r.mobile || '',
      aadhar: r.aadhar || '',
      registrationNo: r.registration_no || '',
      vidhansabha: r.vidhansabha || 'Karad South',
      appointmentLocation: r.appointment_location || '',
      appointmentDate: r.appointment_date || '',
      godownVisitDate: r.godown_visit_date || '',
      godownLocation: r.godown_location || '',
      campLocation: r.camp_location || '',
      operatorName: r.operator_name || '',
      karad: {
        village: isKarad ? (r.village || '') : '',
        ganName: r.gan_name || '',
        zpName: r.zp_name || '',
        reference: r.reference || '',
      },
      other: {
        village: !isKarad ? (r.village || '') : '',
        talika: r.taluka || '',
        district: r.district || '',
      },
    };
  };

  const mapToApi = (f, uid) => {
    const isKarad = f.vidhansabha === 'Karad South';
    return {
      userid: uid,
      appointment_type: f.appointmentType,
      name: f.name,
      gender: f.gender,
      mobile: f.mobile,
      aadhar: f.aadhar,
      registration_no: f.registrationNo,
      vidhansabha: f.vidhansabha,
      village: isKarad ? f.karad.village : f.other.village,
      gan_name: isKarad ? f.karad.ganName : null,
      zp_name: isKarad ? f.karad.zpName : null,
      reference: isKarad ? f.karad.reference : null,
      taluka: !isKarad ? f.other.talika : null,
      district: !isKarad ? f.other.district : null,
      appointment_location: f.appointmentLocation,
      appointment_date: f.appointmentDate || null,
      godown_visit_date: f.godownVisitDate || null,
      godown_location: f.godownLocation,
      camp_location: f.campLocation,
      operator_name: f.operatorName,
    };
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [k]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [k]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  }, []);

  const openAdd = useCallback(() => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
    setForm(emptyForm);
    setEditingId(null);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    const isEdit = !!editingId;
    const url = isEdit ? `${API_BASE}/appointments/${editingId}` : `${API_BASE}/appointments`;
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(mapToApi(form, userid)),
      });

      if (res.ok) {
        fetchRecords();
        setForm(emptyForm);
        setEditingId(null);
        setOpen(false);
      } else {
        const errData = await res.json();
        console.error('Save error:', errData);
        alert('Failed to save record.');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error connecting to server.');
    }
  }

  const handleEdit = useCallback((id) => {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    setForm(rec);
    setEditingId(id);
    setOpen(true);
  }, [records]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const res = await fetch(`${API_BASE}/appointments/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        },
      });
      if (res.ok) {
        fetchRecords(); // Refresh list
        if (editingId === id) {
          setForm(emptyForm);
          setEditingId(null);
          setOpen(false);
        }
      } else {
        alert('Failed to delete record.');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  }, [fetchRecords, editingId]);

  const handleFilterChange = useCallback((e, key) => {
    const v = e.target.value || '';
    setFilters((s) => ({ ...s, [key]: v }));
  }, []);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
      if (filters.appointmentType && !m(r.appointmentType).includes(m(filters.appointmentType))) return false;
      if (filters.name && !m(r.name).includes(m(filters.name))) return false;
      if (filters.mobile && !m(r.mobile).includes(m(filters.mobile))) return false;
      if (filters.aadhar && !m(r.aadhar).includes(m(filters.aadhar))) return false;
      if (filters.vidhansabha && !m(r.vidhansabha).includes(m(filters.vidhansabha))) return false;
      if (filters.appointmentDate && !m(r.appointmentDate).includes(m(filters.appointmentDate))) return false;
      if (filters.godownVisitDate && !m(r.godownVisitDate).includes(m(filters.godownVisitDate))) return false;
      if (filters.godownLocation && !m(r.godownLocation).includes(m(filters.godownLocation))) return false;
      return true;
    });
  }, [records, filters]);

  const isKarad = form.vidhansabha === 'Karad South';

  const searchInputStyle = {
    bgcolor: 'white',
    borderRadius: 1,
    px: 0.5,
    border: '1px solid black'
  };

  const searchInputProps = {
    disableUnderline: true
  };

  const searchInputPropsWithIcon = {
    disableUnderline: true,
    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>
  };

  const headerCellStyle = {
    bgcolor: '#0b4a6f',
    color: 'white',
    fontWeight: 'bold',
    py: 1.5
  };

  const filterCellStyle = {
    bgcolor: '#e1f5fe',
    py: 1
  };

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>Appointments</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add Appointment
          </Button>
        </Box>

        {useMemo(() => (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellStyle}>Type</TableCell>
                  <TableCell sx={headerCellStyle}>Name</TableCell>
                  <TableCell sx={headerCellStyle}>Gender</TableCell>
                  <TableCell sx={headerCellStyle}>Mobile</TableCell>
                  <TableCell sx={headerCellStyle}>Aadhar</TableCell>
                  <TableCell sx={headerCellStyle}>Registration No</TableCell>
                  <TableCell sx={headerCellStyle}>Vidhansabha</TableCell>
                  <TableCell sx={headerCellStyle}>Appointment Date</TableCell>
                  <TableCell sx={headerCellStyle}>Godown Visit Date</TableCell>
                  <TableCell sx={headerCellStyle}>Godown Location</TableCell>
                  <TableCell sx={headerCellStyle}>Camp Location</TableCell>
                  <TableCell align="right" sx={headerCellStyle}>Actions</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.appointmentType} onChange={(e) => handleFilterChange(e, 'appointmentType')} sx={searchInputStyle} InputProps={searchInputPropsWithIcon} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.name} onChange={(e) => handleFilterChange(e, 'name')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle} />
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.mobile} onChange={(e) => handleFilterChange(e, 'mobile')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.aadhar} onChange={(e) => handleFilterChange(e, 'aadhar')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" onChange={(e) => handleFilterChange(e, 'registrationNo')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.vidhansabha} onChange={(e) => handleFilterChange(e, 'vidhansabha')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.appointmentDate} onChange={(e) => handleFilterChange(e, 'appointmentDate')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.godownVisitDate} onChange={(e) => handleFilterChange(e, 'godownVisitDate')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" value={filters.godownLocation} onChange={(e) => handleFilterChange(e, 'godownLocation')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle}><TextField size="small" variant="standard" onChange={(e) => handleFilterChange(e, 'campLocation')} sx={searchInputStyle} InputProps={searchInputProps} placeholder="Search" /></TableCell>
                  <TableCell sx={filterCellStyle} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                    <TableCell>{r.appointmentType}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                    <TableCell>{r.gender}</TableCell>
                    <TableCell>{r.mobile}</TableCell>
                    <TableCell>{r.aadhar}</TableCell>
                    <TableCell>{r.registrationNo}</TableCell>
                    <TableCell>{r.vidhansabha}</TableCell>
                    <TableCell>{r.appointmentDate}</TableCell>
                    <TableCell>{r.godownVisitDate}</TableCell>
                    <TableCell>{r.godownLocation}</TableCell>
                    <TableCell>{r.campLocation}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)} sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>No records found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ), [filtered, filters, handleFilterChange, handleEdit, handleDelete])}
      </Paper>

      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Appointment' : 'Add Appointment'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <FormControl>
              <InputLabel id="appt-type-label">Appointment Type</InputLabel>
              <Select labelId="appt-type-label" label="Appointment Type" name="appointmentType" value={form.appointmentType} onChange={handleChange}>
                <MenuItem value="">-- Select --</MenuItem>
                {appointmentTypes.map((a) => (
                  <MenuItem key={a.code} value={a.label}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField name="name" label="Name" value={form.name} onChange={handleChange} sx={{ flex: '1 1 auto' }} required />
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
              <TextField name="mobile" label="Mobile No" value={form.mobile} onChange={handleChange} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField name="aadhar" label="Aadhar No" value={form.aadhar} onChange={handleChange} sx={{ flex: '1 1 60%' }} />
              <TextField name="registrationNo" label="Registration No" value={form.registrationNo} onChange={handleChange} sx={{ flex: '1 1 40%' }} />
            </Box>

            <FormControl>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {isKarad ? (
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField name="karad.village" label="Village" value={form.karad.village} onChange={handleChange} />
                <TextField name="karad.ganName" label="Gan Name" value={form.karad.ganName} onChange={handleChange} />
                <TextField name="karad.zpName" label="ZP Name" value={form.karad.zpName} onChange={handleChange} />
                <TextField name="karad.reference" label="Reference" value={form.karad.reference} onChange={handleChange} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField name="other.village" label="Village" value={form.other.village} onChange={handleChange} />
                <TextField name="other.talika" label="Talika" value={form.other.talika} onChange={handleChange} />
                <TextField name="other.district" label="District" value={form.other.district} onChange={handleChange} />
              </Box>
            )}

            <TextField name="appointmentLocation" label="Appointment taken location" value={form.appointmentLocation} onChange={handleChange} />

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField name="appointmentDate" label="Appointment Date" value={form.appointmentDate} onChange={handleChange} type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 33%' }} />
              <TextField name="godownVisitDate" label="Godown Visit Date" value={form.godownVisitDate} onChange={handleChange} type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 33%' }} />
              <TextField name="godownLocation" label="Godown Location" value={form.godownLocation} onChange={handleChange} sx={{ flex: '1 1 34%' }} />
            </Box>

            <TextField name="campLocation" label="Camp Location" value={form.campLocation} onChange={handleChange} />

            <TextField name="operatorName" label="Appoint.Taken Operator Name" value={form.operatorName} onChange={handleChange} />

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingId ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
